import { MessageDTO } from '../../../types';
import { TripDTO } from '../types/trip';
import { MAPBOX_API_KEY } from '../../../utils/constants';
import { MapboxTripMapMatcher } from '../../../services/mapbox-matcher.service';
import { computeGPSSignalMetrics } from '../../../utils/computeGPSSignalMetrics';
import { computeGSMSignalMetrics } from '../../../utils/computeGSMSignalMetrics';

const mapboxMatcher = new MapboxTripMapMatcher({
  accessToken: MAPBOX_API_KEY,
  maxPointsPerBatch: 100,
  minPointsThreshold: 20,
  radiusMeters: 30,
});

const isMessagesRangeAcceptable = (messageCount: number) =>
  messageCount > 0 && messageCount < 1500;

const isSingleTrip = (trips: TripDTO[]) => trips.length === 1;

const appendMissingKeysToMessage = (message: MessageDTO) => ({
  ...message,
  timestamp: new Date(message.createdAt).getTime(),
  gpsSignalMetrics: computeGPSSignalMetrics(message.satellites),
  gsmSignalMetrics: computeGSMSignalMetrics(message.gsmSignal),
});

export async function parseTripsWithMessages(
  trips: TripDTO[],
): Promise<TripDTO[]> {
  const messageList = trips
    .filter(
      (trip) =>
        trip.startLatitude !== trip.endLatitude &&
        trip.startLongitude !== trip.endLongitude,
    )
    .map((trip) => {
      // Keep only messages with speed and parse timestamp
      const messages = (trip.messages || [])
        .filter(({ speed }) => speed)
        .map(appendMissingKeysToMessage);

      return {
        ...trip,
        messages,
        timestamp: new Date(trip.startTime).getTime(),
      };
    })
    .sort((a, b) => a.timestamp - b.timestamp);

  // Check if we should normalize the trips
  const totalMessageCount = trips.reduce(
    (acc, trip) => acc + (trip.messages || []).length,
    0,
  );

  if (isMessagesRangeAcceptable(totalMessageCount)) {
    if (isSingleTrip(messageList)) {
      await mapboxMatcher.normalizeTrip(messageList[0]);
    } else {
      await mapboxMatcher.normalizeTrips(messageList);
    }
  }

  return messageList;
}
